import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Highlight from "@tiptap/extension-highlight";
import Placeholder from "@tiptap/extension-placeholder";
import { Bold, Italic, Highlighter, List, ListOrdered, Link2, ImagePlus, Heading2, Heading3, Undo2, Redo2, Images } from "lucide-react";

// Éditeur WYSIWYG Tiptap (v1.5 — blog immersif). Sortie : HTML propre.
// Images insérées UNIQUEMENT depuis la Médiathèque centrale (source unique).
export const RichTextEditor = ({ value, onChange, onPickMedia, testId = "rte", editorRef }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false }),
      Image,
      Highlight,
      Placeholder.configure({ placeholder: "Rédigez le contenu de l'article…" }),
    ],
    content: value || "",
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: {
      attributes: {
        class: "prose-ae min-h-64 max-w-none rounded-b-lg border border-t-0 border-border bg-white px-4 py-3 text-sm focus:outline-none",
        "data-testid": `${testId}-content`,
      },
    },
  });

  useEffect(() => { if (editorRef && editor) editorRef.current = editor; }, [editor, editorRef]);

  // Synchronise quand on ouvre un autre article (édition)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) editor.commands.setContent(value || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value === "" ? "" : undefined]);

  if (!editor) return null;

  const Btn = ({ active, onClick, title, children, tid }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={`${testId}-${tid}`}
      className={`rounded-md p-2 transition-colors ${active ? "bg-deepgreen text-cream" : "text-deepgreen hover:bg-sage-light"}`}
    >
      {children}
    </button>
  );

  const addLink = () => {
    const url = window.prompt("URL du lien (https://…) :");
    if (!url) return;
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 rounded-t-lg border border-border bg-cream px-2 py-1.5" data-testid={`${testId}-toolbar`}>
        <Btn active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Titre 2" tid="h2"><Heading2 className="h-4 w-4" /></Btn>
        <Btn active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Titre 3" tid="h3"><Heading3 className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Gras" tid="bold"><Bold className="h-4 w-4" /></Btn>
        <Btn active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italique" tid="italic"><Italic className="h-4 w-4" /></Btn>
        <Btn active={editor.isActive("highlight")} onClick={() => editor.chain().focus().toggleHighlight().run()} title="Surligné" tid="highlight"><Highlighter className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces" tid="ul"><List className="h-4 w-4" /></Btn>
        <Btn active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée" tid="ol"><ListOrdered className="h-4 w-4" /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn active={editor.isActive("link")} onClick={addLink} title="Lien" tid="link"><Link2 className="h-4 w-4" /></Btn>
        {onPickMedia && (
          <Btn onClick={onPickMedia} title="Insérer une image de la Médiathèque" tid="image"><Images className="h-4 w-4" /></Btn>
        )}
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn onClick={() => editor.chain().focus().undo().run()} title="Annuler" tid="undo"><Undo2 className="h-4 w-4" /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} title="Rétablir" tid="redo"><Redo2 className="h-4 w-4" /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

// Insère une image de la médiathèque dans l'éditeur courant (helper pour le dialog)
export const insertImageInEditor = (editor, url, alt = "") => {
  if (editor && url) editor.chain().focus().setImage({ src: url, alt }).run();
};
